import axios from 'axios';
import { appConfig } from '../config/config';
import { GamesResponse, Entry, Standing, AdminStats } from '../types';

const api = axios.create({
  baseURL: `${appConfig.functionsUrl}/api`,
  timeout: 30000,
});

// Games API
export const fetchGames = async (date: string): Promise<GamesResponse> => {
  const response = await api.get(`/games?date=${date}`);
  return response.data;
};

// Entries API
export const createEntry = async (entryData: {
  date: string;
  xHandle: string;
  xrplAddress: string;
  tieBreakTotalScore: number;
  picks: Array<{ gameId: string; pick: 'home' | 'away' }>;
}): Promise<Entry> => {
  const response = await api.post('/entries', entryData);
  return response.data;
};

export const getEntry = async (entryId: string): Promise<Entry> => {
  const response = await api.get(`/entries/${entryId}`);
  return response.data;
};

// Leaderboard API
export const getLeaderboard = async (date: string): Promise<Standing[]> => {
  const response = await api.get(`/leaderboard?date=${date}`);
  return response.data;
};

// Admin API
export const getAdminStats = async (password: string, date: string): Promise<AdminStats> => {
  const response = await api.get(`/admin/stats?date=${date}`, {
    headers: { 'X-Admin-Password': password }
  });
  return response.data;
};

export const fetchDateEntries = async (password: string, date: string): Promise<Entry[]> => {
  const response = await api.get(`/admin/entries?date=${date}`, {
    headers: { 'X-Admin-Password': password }
  });
  return response.data;
};

export const finalizeResults = async (password: string, date: string): Promise<void> => {
  await api.post(`/admin/finalize`, { date }, {
    headers: { 'X-Admin-Password': password }
  });
};

export const updateGameResults = async (password: string): Promise<void> => {
  await api.post(`/admin/update-games`, {}, {
    headers: { 'X-Admin-Password': password }
  });
};

// Payment API - EXACT SAME AS WORKING MLB VERSION
export const createPaymentRequest = async (entryId: string): Promise<{
  payloadId: string;
  uuid: string;
  qrCode?: string;
  deepLink?: string;
  refs?: any;
  next?: any;
}> => {
  console.log('🔥 API: createPaymentRequest called with entryId:', entryId);
  console.log('🔥 API: Base URL:', api.defaults.baseURL);
  console.log('🔥 API: Making request to /payments with payload:', { entryId });
  
  try {
    const response = await api.post(`/payments`, { entryId });
    console.log('🔥 API: Response received:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('🔥 API: Payment request failed:', error);
    console.error('🔥 API: Error response:', error.response?.data);
    console.error('🔥 API: Error status:', error.response?.status);
    throw error;
  }
};

export const createTrustlineSetup = async (entryId: string): Promise<{
  payloadId: string;
  uuid: string;
  qrCode?: string;
  deepLink?: string;
  refs?: any;
  next?: any;
  type: string;
}> => {
  const response = await api.post(`/payments/trustline`, { entryId });
  return response.data;
};

export const verifyPayment = async (paymentId: string): Promise<{
  success: boolean;
  state: 'pending' | 'completed' | 'cancelled' | 'expired';
  signed: boolean;
  resolved: boolean;
  cancelled: boolean;
  expired: boolean;
  txHash?: string | null;
  paymentId: string;
}> => {
  console.log('🔥 API: verifyPayment called with paymentId:', paymentId);
  // Use the status endpoint instead of verify
  const response = await api.get(`/payments/${paymentId}/status`);
  console.log('🔥 API: verifyPayment response:', response.data);
  return response.data;
};

export const checkPaymentStatus = async (paymentId: string): Promise<{
  success: boolean;
  status: any;
  response: any;
}> => {
  const response = await api.get(`/payments/status/${paymentId}`);
  return response.data;
};

// Admin functions
export const markEntryAsPaid = async (entryId: string): Promise<{
  success: boolean;
  message: string;
}> => {
  const response = await api.put(`/payments/${entryId}/confirm`);
  return response.data;
};

export const deleteEntry = async (entryId: string): Promise<{
  success: boolean;
  message: string;
  deletedPicks: number;
}> => {
  const response = await api.delete(`/entries/${entryId}`);
  return response.data;
};

export default api;
